/**
 * Creating a report, in two questions.
 *
 * It used to be one: pick a kind, name it, done — so the only way to build on
 * something that already existed was to make a new one and re-do the work, or to
 * press "Branch" in Report Studio and end up with "Active Pipeline (branch)"
 * beside "Active Pipeline". Both roads lead to four reports with the same name
 * and nobody able to say which one the committee reads.
 *
 * So after the kind is chosen the dialog asks the question that decides what
 * happens: change one of these, or start fresh? The reports of that kind are
 * listed by name and last edit, because "is there already one of these" is not
 * a question anybody can answer from memory.
 */
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Pencil, Plus } from 'lucide-react'
import DialogShell from '@/components/crm/DialogShell'
import { reportDefinitionsService, type ReportDefinition, type ReportTypeId } from '@/services/reportDefinitions'
import { DEFAULT_COLUMNS, EMPTY_FILTERS } from '../../pipeline-intelligence/types'
import { REPORT_TYPES, initialFilters, reportTypeSpec } from '../reportTypes'
import { formatAgo } from './format'
import { Btn, Eyebrow, Field, INPUT } from './ui'
import { requestErrorMessage } from './useReportActions'
import clsx from 'clsx'

export function NewReportDialog({
  existing,
  onClose,
  onCreated,
  onOpenExisting,
}: {
  existing: ReportDefinition[]
  onClose: () => void
  onCreated: (definition: ReportDefinition) => Promise<void> | void
  onOpenExisting: (definition: ReportDefinition) => void
}) {
  const [step, setStep] = useState<'kind' | 'choice'>('kind')
  const [chosen, setChosen] = useState<ReportTypeId>('committee_book')
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const sameKind = existing.filter((definition) => definition.reportType === chosen)
  const spec = reportTypeSpec(chosen)

  const create = useMutation({
    mutationFn: () =>
      reportDefinitionsService.create({
        name: name.trim() || spec.name,
        reportType: chosen,
        // A pipeline report opens in Report Studio, which reads every workspace filter and the
        // column list: it starts from the standard book rather than from an empty definition.
        ...(chosen === 'pipeline'
          ? { columns: [...DEFAULT_COLUMNS], filters: { ...EMPTY_FILTERS } }
          : { filters: initialFilters(chosen) }),
        note: 'Initial version',
      }),
    onSuccess: (definition) => void onCreated(definition),
    onError: async (cause) => setError(await requestErrorMessage(cause, 'The report could not be created.')),
  })

  return (
    <DialogShell
      label={step === 'kind' ? 'Create a report' : `${spec.name}: change one, or start fresh?`}
      onClose={onClose}
      width="2xl"
      testId="new-report-dialog"
      footer={
        <>
          {step === 'choice' ? (
            <Btn variant="quiet" onClick={() => setStep('kind')} className="mr-auto">
              Back
            </Btn>
          ) : null}
          <Btn variant="quiet" onClick={onClose}>
            Cancel
          </Btn>
          {step === 'kind' ? (
            <Btn variant="primary" onClick={() => setStep('choice')}>
              Next
            </Btn>
          ) : (
            <Btn
              variant="primary"
              busy={create.isPending}
              icon={<Plus className="h-3.5 w-3.5" aria-hidden />}
              onClick={() => {
                setError(null)
                create.mutate()
              }}
            >
              Create new and open
            </Btn>
          )}
        </>
      }
    >
      <div className="max-h-[70vh] overflow-auto p-4">
        {step === 'kind' ? (
          <>
            <Eyebrow>What kind of report</Eyebrow>
            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              {REPORT_TYPES.map((entry) => {
                const Icon = entry.icon
                const active = chosen === entry.id
                const count = existing.filter((definition) => definition.reportType === entry.id).length
                return (
                  <button
                    key={entry.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setChosen(entry.id)}
                    className={clsx(
                      'rounded-lg border p-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFD86B]',
                      active ? 'border-[#6B5A27] bg-[#171408]' : 'border-[#222] bg-[#0C0C0C] hover:border-[#3A3A3A]',
                    )}
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-[#FFD86B]" aria-hidden />
                      <span className="text-[13px] font-medium text-[#F5F3EE]">{entry.name}</span>
                      <span className="ml-auto text-[10px] uppercase tracking-wider text-[#8C8C8C]">{entry.code}</span>
                    </div>
                    <p className="mt-1.5 text-[12px] leading-relaxed text-[#9A9A9A]">{entry.description}</p>
                    {/* Said here, before the choice, because "you already have three of these"
                        is the fact most likely to change what somebody does next. */}
                    {count > 0 ? (
                      <p className="mt-1.5 text-[12px] text-[#FFD86B]">
                        {count} already {count === 1 ? 'exists' : 'exist'}
                      </p>
                    ) : null}
                  </button>
                )
              })}
            </div>
            {chosen === 'pipeline' ? (
              <p className="mt-3 rounded-lg border border-[#4A3F1E] bg-[#0E0D09] px-3 py-2 text-[12px] leading-relaxed text-[#E8D9A8]">
                Pipeline reports are designed in Report Studio: columns, per-column filters, grouping, totals, styling
                and paper size, with the export matching the screen exactly. It opens as soon as you name the report.
              </p>
            ) : null}
          </>
        ) : (
          <div data-testid="new-report-choice">
            {sameKind.length > 0 ? (
              <>
                <Eyebrow>Change one you already have</Eyebrow>
                <p className="mt-1 text-[12px] leading-relaxed text-[#8F8F8F]">
                  Editing an existing report keeps one {spec.name} on the desk instead of several with the same name.
                  Every change is versioned, so the previous one can be restored.
                </p>
                <ul className="mt-2 grid gap-1.5">
                  {sameKind.map((definition) => (
                    <li key={definition.id}>
                      <button
                        type="button"
                        onClick={() => onOpenExisting(definition)}
                        className="flex w-full items-center gap-2 rounded-lg border border-[#222] bg-[#0C0C0C] px-3 py-2 text-left transition-colors hover:border-[#6B5A27] hover:bg-[#171408] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FFD86B]"
                      >
                        <Pencil className="h-3.5 w-3.5 shrink-0 text-[#FFD86B]" aria-hidden />
                        <span className="min-w-0">
                          <span className="block truncate text-[13px] font-medium text-[#F5F3EE]">{definition.name}</span>
                          <span className="block truncate text-[12px] text-[#8F8F8F]">
                            v{definition.version} · {definition.ownerEmail} · edited {formatAgo(definition.updatedAt)}
                          </span>
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="my-4 flex items-center gap-3" aria-hidden>
                  <span className="h-px flex-1 bg-[#222]" />
                  <span className="text-[10px] uppercase tracking-[.14em] text-[#808080]">or</span>
                  <span className="h-px flex-1 bg-[#222]" />
                </div>
              </>
            ) : (
              <p className="mb-4 rounded-lg border border-[#222] bg-[#0C0C0C] px-3 py-2 text-[12.5px] leading-relaxed text-[#C9C9C9]">
                There is no {spec.name} yet. This will be the first.
              </p>
            )}

            <Eyebrow>Start fresh</Eyebrow>
            <div className="mt-2">
              <Field label="Name" htmlFor="new-report-name">
                <input
                  id="new-report-name"
                  className={INPUT}
                  value={name}
                  placeholder={spec.name}
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
            </div>
          </div>
        )}

        {error ? (
          <div role="alert" className="mt-3 rounded-lg border border-[#4A3220] bg-[#140E0A] px-3 py-2 text-[12.5px] text-[#F0B08A]">
            {error}
          </div>
        ) : null}
      </div>
    </DialogShell>
  )
}
